import { useEffect, useState } from "react";
import { Box, Chip } from "@mui/material";
import { DataGridTable } from "../DataGridTable/DataGridTable";
import { ColumnText } from "../DataGridTable/ColumnText";
import { ColumnButton } from "../DataGridTable/ColumnButton";
import { HeaderGridTable } from "../DataGridTable/HeaderGridTable";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Filter from "./Filters";
import {
  SupportTickets,
  resetFilters,
  setFilters,
  setFiltersDialog,
} from "../../app/slices/supportTicketsSlice";
import { useDebounce } from "use-debounce";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

const statusChipProps = (status) => {
  switch (status) {
    case "new":
      return { label: "New", color: "info" };
    case "in_progress":
      return { label: "In Progress", color: "warning" };
    case "resolved":
      return { label: "Resolved", color: "success" };
    default:
      return { label: status, color: "default" };
  }
};

export const Table = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    tickets: { data, loading, filters, total, dialog },
  } = useSelector((state) => state.supportTickets);

  const [search, setSearch] = useState(null);
  const [debouncedSearch] = useDebounce(search, 600);

  const columns = [
    {
      field: "ticket_number",
      headerName: "Ticket #",
      headerClassName: "employer-grid--header",
      flex: 1.2,
      renderCell: (params) => (
        <ColumnText
          text={params.row.ticket_number}
          variant="subtitle2"
          fontWeight="bold"
        />
      ),
    },
    {
      field: "title",
      headerName: "Title",
      headerClassName: "employer-grid--header",
      flex: 1.8,
      renderCell: (params) => (
        <ColumnText
          text={params.row.title}
          variant="subtitle2"
          fontWeight="bold"
        />
      ),
    },
    {
      field: "user",
      headerName: "User",
      headerClassName: "employer-grid--header",
      flex: 1.4,
      renderCell: (params) => (
        <Box display="flex" flexDirection="column">
          <ColumnText
            text={params.row.user?.name}
            variant="subtitle2"
            fontWeight="bold"
          />
          <ColumnText
            text={params.row.user?.email}
            variant="caption"
            color="gray"
          />
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      headerClassName: "employer-grid--header",
      flex: 1,
      renderCell: (params) => {
        const chip = statusChipProps(params.row.status);
        return <Chip label={chip.label} color={chip.color} size="small" />;
      },
    },
    {
      field: "comments_count",
      headerName: "Comments",
      headerClassName: "employer-grid--header",
      flex: 0.8,
      renderCell: (params) => (
        <ColumnText
          text={params.row.comments_count}
          variant="subtitle2"
          fontWeight="bold"
        />
      ),
    },
    {
      field: "created_at",
      headerName: "Created At",
      headerClassName: "employer-grid--header",
      flex: 1.1,
      renderCell: (params) => (
        <ColumnText
          text={dayjs(params.row.created_at).format("YYYY-MM-DD HH:mm")}
          variant="subtitle2"
          fontWeight="bold"
        />
      ),
    },
    {
      field: "operations",
      headerName: "Operations",
      headerClassName: "employer-grid--header",
      sortable: false,
      flex: 0.7,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <ColumnButton
            title="View"
            color="primary"
            onClick={() => navigate(`/tickets/${params.row.id}`)}
            icon={<VisibilityIcon color="primary" />}
          />
        </Box>
      ),
    },
  ];

  const fetchData = () => {
    dispatch(SupportTickets.index());
  };

  useEffect(() => {
    if (debouncedSearch !== null) {
      dispatch(setFilters({ search: debouncedSearch }));
    }
  }, [debouncedSearch]);

  useEffect(() => {
    fetchData();
  }, [filters.skip, filters.take, filters.page, filters.search, filters.status]);

  useEffect(() => {
    return () => {
      dispatch(resetFilters());
    };
  }, []);

  return (
    <>
      <Box m={2}>
        <HeaderGridTable
          title="Support Tickets"
          icon={<ConfirmationNumberIcon size={25} color="primary" />}
          isFilter={true}
          fetchData={fetchData}
          handleFilter={() => dispatch(setFiltersDialog())}
          handleSearch={(e) => setSearch(e.target.value)}
        />
        <Box mt={2}>
          <DataGridTable
            rowCount={total}
            rows={data}
            loading={loading}
            page={filters.page}
            columns={columns}
            pageSize={filters.take}
            onPageChange={(newPage) => {
              dispatch(
                setFilters({
                  skip: newPage * filters.take,
                  page: newPage,
                })
              );
            }}
            onPageSizeChange={(newPageSize) => {
              dispatch(setFilters({ take: newPageSize }));
              if (filters.skip === 0) {
                dispatch(SupportTickets.index());
              } else {
                dispatch(setFilters({ skip: 0, page: 0 }));
              }
            }}
          />
        </Box>
        <Filter fetchData={fetchData} />
      </Box>
    </>
  );
};

export default Table;
